import {
  isRef, isReactive, reactive, toRefs,
} from 'vue';
import useVuelidate from '@vuelidate/core';
import {
  required, minLength, maxLength, helpers,
} from '@vuelidate/validators';
import { AvailableTypeEnum } from './customData.interfaces';
import type { Field } from './customData.interfaces';

// validation rules for a custom field configuration
export function validateFieldWithVuelidate(t: (key: string) => string, field: Field, existingFieldNames: string[] = []) {
  // helper to get all valid type values
  const allowedTypes = Object.values(AvailableTypeEnum);

  // unicity rule for the name (case insensitive)
  const uniqueName = helpers.withMessage(
    t('customData.validation.name_unique'),
    (value: string) => !existingFieldNames.map((n) => n.trim().toLowerCase()).includes((value || '').trim().toLowerCase()),
  );

  // required and not empty
  const notEmpty = helpers.withMessage(
    t('customData.validation.name_required'),
    (value: string) => !!(value && value.trim()),
  );

  // valid type
  const validType = helpers.withMessage(
    t('customData.validation.type_invalid'),
    (value: AvailableTypeEnum) => allowedTypes.includes(value),
  );

  // description length (0-200)
  const descriptionLength = helpers.withMessage(
    t('customData.validation.description_length'),
    (value: string) => !value || value.length <= 200,
  );

  // if unique_key is true, required must be true
  const uniqueKeyRequiresRequired = helpers.withMessage(
    t('customData.validation.unique_key_required'),
    (_: unknown, vm: any) => {
      if (vm.unique_key.value) {
        return !!vm.required.value;
      }
      return true;
    },
  );

  // if type is 'list', options must be defined and default must be in options
  const optionsRequired = helpers.withMessage(
    t('customData.validation.options_required'),
    (_: unknown, vm: any) => {
      if (vm.type.value === AvailableTypeEnum.LIST) {
        return Array.isArray(vm.options.value) && vm.options.value.length > 0;
      }
      return true;
    },
  );

  const defaultRequired = helpers.withMessage(
    t('customData.validation.default_required'),
    (_: unknown, vm: any) => {
      if (vm.type.value === AvailableTypeEnum.LIST) {
        return typeof vm.default.value !== 'undefined' && vm.default.value !== null && vm.default.value !== '';
      }
      return true;
    },
  );

  const defaultInOptions = helpers.withMessage(
    t('customData.validation.default_in_options'),
    (_: unknown, vm: any) => {
      if (vm.type.value === AvailableTypeEnum.LIST) {
        return Array.isArray(vm.options.value) && vm.options.value.includes(vm.default.value);
      }
      return true;
    },
  );

  // label validation: required and not empty
  const labelNotEmpty = helpers.withMessage(
    t('customData.validation.label_required'),
    (value: string) => !!(value && value.trim()),
  );

  // Validation schema
  const rules = {
    name: {
      notEmpty,
      minLength: minLength(2),
      maxLength: maxLength(50),
      uniqueName,
    },
    label: {
      labelNotEmpty,
      minLength: minLength(2),
      maxLength: maxLength(50),
    },
    type: {
      required,
      validType,
    },
    required: {},
    unique_key: {
      uniqueKeyRequiresRequired,
    },
    description: {
      descriptionLength,
    },
    default: {
      defaultRequired,
      defaultInOptions,
    },
    options: {
      optionsRequired,
    },
  };

  // complete the field with the default values
  const fullField = {
    ...field,
    name: field.name ?? '',
    label: field.label ?? '',
    type: field.type ?? '',
    required: field.required ?? false,
    unique_key: field.unique_key ?? false,
    description: field.description ?? '',
    options: field.options ?? [],
    default: field.default ?? null,
  };

  const baseReactiveField = isRef(fullField) || isReactive(fullField) ? fullField : reactive(fullField);
  const reactiveField = toRefs(baseReactiveField);
  const v$ = useVuelidate(rules, reactiveField);
  v$.value.$touch();

  // Group errors by field
  const errors = Object.keys(v$.value).reduce((acc, key) => {
    if (v$.value[key]?.$errors?.length) {
      acc[key] = v$.value[key].$errors.map((e: any) => e.$message);
    }
    return acc;
  }, {} as Record<string, string[]>);

  return {
    valid: !v$.value.$invalid,
    errors,
  };
}

export async function validateSettingsWithVuelidate(settings: { name: string; description: string }) {
  const rules = {
    name: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(35),
    },
    description: {
      maxLength: maxLength(500),
    },
  };

  const v$ = useVuelidate(rules, settings);
  const success = await v$.value.$validate();

  // Extract plain error data from Vuelidate to avoid passing reactive internals as props
  const errors: Record<string, { $errors: Array<{ $message: string; $propertyPath: string }>; $silentErrors: Array<{ $message: string; $propertyPath: string }> }> = {};
  for (const key of Object.keys(rules)) {
    if (v$.value[key]) {
      errors[key] = {
        $errors: (v$.value[key].$errors || []).map((e: any) => ({ $message: e.$message, $propertyPath: e.$propertyPath })),
        $silentErrors: (v$.value[key].$silentErrors || []).map((e: any) => ({ $message: e.$message, $propertyPath: e.$propertyPath })),
      };
    }
  }

  return {
    valid: success,
    errors,
  };
}
